import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  GearSixIcon,
  MoonIcon,
  ShieldCheckIcon,
  SignOutIcon,
  StorefrontIcon,
  SunIcon,
  TranslateIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { useAuth } from '@/auth/AuthProvider';
import { useTenant } from '@/tenant/TenantProvider';
import { setLanguage } from '@/i18n';
import { useTheme } from '../theme';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Tooltip,
} from '@/ui';

export function ThemeToggle() {
  const { t } = useTranslation('shell');
  const { resolved, toggle } = useTheme();
  const label = resolved === 'dark' ? t('theme.toLight') : t('theme.toDark');
  return (
    <Tooltip content={label}>
      <IconButton label={label} size="sm" onClick={toggle}>
        {resolved === 'dark' ? <SunIcon /> : <MoonIcon />}
      </IconButton>
    </Tooltip>
  );
}

export function LanguageToggle() {
  const { t, i18n } = useTranslation('shell');
  const next = i18n.language === 'el' ? 'en' : 'el';
  const label = t('language.switchTo', { lang: t(`language.${next}`) });
  return (
    <Tooltip content={label}>
      <IconButton label={label} size="sm" onClick={() => void setLanguage(next)}>
        <span className="text-[0.6875rem] font-bold uppercase tracking-wide">
          {i18n.language === 'el' ? 'ΕΛ' : 'EN'}
        </span>
      </IconButton>
    </Tooltip>
  );
}

export function UserBar({
  variant = 'portal',
  onNavigate,
}: {
  variant?: 'portal' | 'admin';
  onNavigate?: () => void;
}) {
  const { t, i18n } = useTranslation('shell');
  const { user, signOut } = useAuth();
  const { isPlatformAdmin, me } = useTenant();
  const { pref, setPref } = useTheme();
  const navigate = useNavigate();
  const go = (to: string) => {
    onNavigate?.();
    navigate(to);
  };
  const name = me?.user.displayName || user?.displayName || user?.email?.split('@')[0] || '';
  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        {/* Sits at the foot of the sidebar (brand field): ink tokens resolve to on-field there. */}
        <DropdownMenuTrigger className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-1.5 py-1.5 text-left transition-colors hover:bg-field-raised focus-visible:outline-2 focus-visible:outline-focus data-[state=open]:bg-field-raised">
          <Avatar name={name || user?.email} size="sm" />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-sm font-semibold text-ink">{name}</span>
            <span className="block truncate text-xs text-ink-muted">{user?.email}</span>
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-64">
          <DropdownMenuLabel>{t('account')}</DropdownMenuLabel>
          {variant === 'portal' ? (
            <>
              <DropdownMenuItem icon={<GearSixIcon />} onSelect={() => go('/settings')}>
                {t('menu.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem icon={<UsersThreeIcon />} onSelect={() => go('/settings/team')}>
                {t('menu.team')}
              </DropdownMenuItem>
              {isPlatformAdmin && (
                <DropdownMenuItem icon={<ShieldCheckIcon />} onSelect={() => go('/platform-admin')}>
                  {t('menu.platformAdmin')}
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <DropdownMenuItem icon={<StorefrontIcon />} onSelect={() => go('/')}>
              {t('menu.backToPortal')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t('theme.label')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={pref} onValueChange={(v) => setPref(v as typeof pref)}>
            <DropdownMenuRadioItem value="light">{t('theme.light')}</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">{t('theme.dark')}</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system">{t('theme.system')}</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            icon={<TranslateIcon />}
            onSelect={() => void setLanguage(i18n.language === 'el' ? 'en' : 'el')}
          >
            {i18n.language === 'el' ? 'English' : 'Ελληνικά'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            icon={<SignOutIcon />}
            tone="danger"
            onSelect={() => void signOut().then(() => navigate('/login'))}
          >
            {t('menu.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LanguageToggle />
      <ThemeToggle />
    </div>
  );
}
